import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import { JSONPatchDocument, ModelWithMeta, Opts, PagedResponse, QueryParams, ValidatedResponse } from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { AdditionalEvidenceRequest } from "../domain/AdditionalEvidenceRequest";
import { Attachment } from "../domain/Attachment";
import { DateEvent } from "../domain/DateEvent";
import { Scope, toApiModel, fromApiModel } from "../domain/Scope";
import { ScopeApplication, ScopeApplicationSummary } from "../domain/ScopeApplication";

export class ScopeExpansionService extends BaseRemoteService {
  public async search(id: string, params: QueryParams = {}, opts: Opts): Promise<PagedResponse<ScopeApplicationSummary>> {
    const url = `${this.apiRoot}/api/organisations/${id}/expansionapplicationsummary/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<ScopeApplicationSummary>>(url, opts);
    return response;
  }


  public async get(orgId: string, id: string, opts: Opts): Promise<{item: ScopeApplication, scopes: Scope[], attachments: Attachment[]}> {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}`;
    const scopeUrl = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/ApplicationScopes`;
    const attachmentUrl = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/attachments`;

    try {
      // todo: this could be one call api needs refactoring
      const [application, scopes, attachments] = await Promise.all([ 
        super.getRequest<ScopeApplication>(url, opts),
        super.getRequest<Scope[]>(scopeUrl, opts),
        super.getRequest<Attachment[]>(attachmentUrl, opts),
      ]);
      const mappedScopes = scopes.map(scope => fromApiModel(scope));
      return {
        item: application,
        scopes: mappedScopes,
        attachments,
      }

    } catch (err) {
      throw err;
    }
  }

  public async create(
    orgId: string,
    item: ScopeApplication,
    scopes: Scope[],
    opts: Opts
  ): Promise<ValidatedResponse<ScopeApplication>> {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications`;
    try {
      const response = await super.postRequest<ScopeApplication>(
        url,
        item,
        opts
      );
      const id = response.id;
      if (scopes && scopes.length) {
        await this.postUpdateScopes(orgId, id, scopes, opts);
      }
      if (item.questionFourAttachments && item.questionFourAttachments.length)
      {
        await this.updateDocuments(orgId, id, item.questionFourAttachments, opts);
      }
      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  private async postUpdateScopes(orgId: string, applicationId: string, scopes: Scope[], opts: Opts) {
    try {
      const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${applicationId}/ApplicationScopes`;
      const mappedScopes = scopes.map(scope => toApiModel(scope, orgId));
      return await super.postRequest<Scope>(
        url,
        mappedScopes,
        opts
      );
    } catch (err) {
      return null;
    }
  }

  private async updateDocuments(orgId: string, applicationId: string, documents: File[], opts: Opts) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${applicationId}/attachments`;
    await super.postRequestFormData(
      url,
      {files: documents},
      opts
    );
  }

  private async deleteDocuments(orgId: string, applicationId: string, documentsToDelete: Attachment[], opts: Opts) {
    const promiseArray: Array<Promise<any>> = [];
    for (const document of documentsToDelete) {
      const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${applicationId}/attachments/${document.blobId}`;
      promiseArray.push(super.deleteRequest(url, opts))
    }
    await Promise.all(promiseArray);
  }

  public async submitUpdate(
    orgId: string, 
    id: string, 
    patchDocument: JSONPatchDocument,
    scopes: Scope[],
    documentsToDelete: Attachment[],
    opts: Opts,
  ): Promise<ValidatedResponse<ScopeApplication>> {
    try {
      await this.update(orgId, id, patchDocument, scopes, documentsToDelete,  opts);
      const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/submit`;
      const response = await this.putRequest<ScopeApplication>(url, null, opts);
      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async submit(
    orgId: string, 
    item: ScopeApplication,
    scopes: Scope[],
    opts: Opts
  ): Promise<ValidatedResponse<ScopeApplication>> {
    try {
      const createdScope = await this.create(orgId, item, scopes, opts);
      const scopeId = createdScope.isValid ? createdScope.data.id : null;
      const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${scopeId}/submit`;
      const response = await this.putRequest<ScopeApplication>(url, null, opts);
      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async update(
    orgId: string, 
    id: string, 
    patchDocument: JSONPatchDocument,
    scopes: Scope[],
    documentsToDelete: Attachment[],
    opts: Opts
  ): Promise<ValidatedResponse<ScopeApplication>> {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}`;
    try {
      const attachmentsPatch = patchDocument.find(patch => patch.path === "/questionFourAttachments");

      await this.postUpdateScopes(orgId, id, scopes, opts);
      if (attachmentsPatch)
      {
        patchDocument = patchDocument.filter(x => x !== attachmentsPatch);
        await this.updateDocuments(orgId, id, attachmentsPatch.value, opts);
      }

      if (documentsToDelete.length) {
        await this.deleteDocuments(orgId, id, documentsToDelete, opts)
      }

      const response = await this.patchRequest<ScopeApplication>(
        url,
        patchDocument,
        opts
      );
      
      return {
        isValid: true,
        data: response,
      };
    } catch (err: any) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async downloadAttachment(
    orgId: string,
    id: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/attachments/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async assign(
    orgId: string,
    id: string,
    assignedToUpn: string,
    assignedToDisplayName: string,
    message: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/assignments`;
    await super.postRequest(
      url,
      { 
        assignTo: {
          upn: assignedToUpn, 
          displayName: assignedToDisplayName 
        },
        message
      },
      opts
    );
    return await this.get(orgId, id, opts);
  }

  public async updateScopes(
    orgId: string,
    id: string,
    modelsWithMeta: ModelWithMeta<Scope>[],
    message: string,
    opts: Opts
  ) {
    const mappedModelWithMeta = modelsWithMeta.map(modelMeta => ({ model: toApiModel(modelMeta.model, orgId), meta: modelMeta.meta}));
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/applicationscopes`;
    await super.putRequest(
      url,
      {
        applicationScopes: mappedModelWithMeta,
        message,
      },
      opts
    );
    return await this.get(orgId, id, opts);
  }

  public async sendNote(
    orgId: string,
    id: string,
    note: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/notes`;
    return await super.postRequest(
      url,
      note,
      opts
    );
  }

  public async sendDocuments(
    orgId: string,
    id: string,
    files: File[],
    documentType: string,
    note: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/documents`;
    return await super.postRequestFormData(url, { documentType, note, files }, opts);
  }

  public async downloadActivityDocument(
    orgId: string,
    id: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/documents/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendDate(
    orgId: string,
    id: string,
    dateEvent: DateEvent,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/dates`;
    return await super.postRequest(url, dateEvent, opts);
  }

  public async closeApplication(
    orgId: string,
    id: string,
    note: string,
    letter: File,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/close`;
    await super.postRequestFormData(url, { letter, note }, opts);
    return await this.get(orgId, id, opts);
  }

  public async downloadCloseDocument(
    orgId: string,
    id: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/close/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendAdditionalEvidence(
    orgId: string,
    id: string,
    evidenceEvent: AdditionalEvidenceRequest,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/evidencerequests`;
    await super.postRequest(url, evidenceEvent, opts);
    return await this.getAdditionalEvidenceRequests(orgId, id, opts);
  }

  public async getAdditionalEvidenceRequests(
    orgId: string,
    id: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/additionalevidence`;
    try {
      return await super.getRequest<AdditionalEvidenceRequest[]>(url, opts);
    } catch (err: any) {
      if (err && err.status === 404) {
        return [];
      }
      throw err;
    }
  }

  public async sendMessage(orgId: string, id: string, message: string, opts: Opts) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/messages`;
    return await super.postRequest(url, message, opts);
  }

  public async withdrawApplication(orgId: string, id: string, note: string, opts: Opts) {
    const url = `${this.apiRoot}/api/organisations/${orgId}/expansionapplications/${id}/activities/withdraw`;
    await super.postRequest(url, note, opts);
    return await this.get(orgId, id, opts);
  }
}